import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight } from 'lucide-react';
import SpecialtyModal from './SpecialtyModal';

// Medical Icons as SVG components - Clean professional line-art style
const ObgynIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    {/* Pregnant woman profile */}
    <circle cx="26" cy="12" r="6" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 20C18 24 16 29 16 34V50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M32 20C34 24 35 28 35 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Pregnant belly curve */}
    <path d="M35 32C39 34 42 37 42 41C42 45 39 47 35 45V50" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M16 50H35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Baby heartbeat */}
    <path d="M36 36L38 33L40 38L42 35L44 37" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Cradle lines */}
    <path d="M20 56C22 54 28 54 30 56" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="25" cy="56" r="2" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const CardiologyIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <path d="M32 56C32 56 8 40 8 24C8 14 16 8 24 8C28 8 32 12 32 12C32 12 36 8 40 8C48 8 56 14 56 24C56 40 32 56 32 56Z" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <path d="M16 32H24L28 24L32 40L36 28L40 32H48" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UrologyIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    {/* Left kidney */}
    <path d="M18 10C12 12 8 18 8 26C8 32 11 35 13 38C15 42 14 47 12 50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M18 10C22 13 24 18 24 26C24 30 22 33 20 35C18 38 18 42 20 46" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Right kidney */}
    <path d="M46 10C52 12 56 18 56 26C56 32 53 35 51 38C49 42 50 47 52 50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M46 10C42 13 40 18 40 26C40 30 42 33 44 35C46 38 46 42 44 46" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Ureters to bladder */}
    <path d="M20 46C24 50 28 52 32 54" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M44 46C40 50 36 52 32 54" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Bladder */}
    <ellipse cx="32" cy="57" rx="7" ry="4" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const MedicineIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <rect x="20" y="12" width="24" height="40" rx="4" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <line x1="20" y1="24" x2="44" y2="24" stroke="currentColor" strokeWidth="2"/>
    <circle cx="32" cy="38" r="6" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2"/>
    <line x1="32" y1="34" x2="32" y2="42" stroke="currentColor" strokeWidth="2"/>
    <line x1="28" y1="38" x2="36" y2="38" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const OrthoIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    {/* Femur bone top - ball joint */}
    <circle cx="20" cy="10" r="5" stroke="currentColor" strokeWidth="2"/>
    <path d="M25 12L30 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Femur shaft */}
    <path d="M28 16L36 38" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    {/* Knee joint */}
    <circle cx="37" cy="40" r="5" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.08"/>
    {/* Tibia shaft */}
    <path d="M35 45L28 58" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    {/* Joint flex lines */}
    <path d="M44 38C46 40 46 42 44 44" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M48 36C51 39 51 43 48 46" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Greater trochanter */}
    <path d="M20 15L24 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const GastroIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    {/* Esophagus */}
    <path d="M28 6V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M34 6V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Stomach body - J shape */}
    <path d="M28 16C22 16 14 20 14 30C14 40 20 46 28 48C32 49 38 48 42 44C46 40 48 34 46 28C44 22 40 16 34 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* Internal fold - greater curvature detail */}
    <path d="M20 28C22 32 26 34 30 34" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Pylorus / duodenum exit */}
    <path d="M42 44L48 48L52 46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* Fundus detail */}
    <path d="M22 20C24 18 28 18 28 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Acid/digestion dots */}
    <circle cx="26" cy="38" r="1.5" fill="currentColor" fillOpacity="0.2"/>
    <circle cx="32" cy="40" r="1.5" fill="currentColor" fillOpacity="0.2"/>
    <circle cx="36" cy="36" r="1.5" fill="currentColor" fillOpacity="0.2"/>
  </svg>
);

const SkinIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    {/* Magnifying glass */}
    <circle cx="26" cy="26" r="14" stroke="currentColor" strokeWidth="2"/>
    <path d="M36 36L50 50" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    {/* Skin layers inside magnifying glass */}
    <path d="M14 28C17 26 20 28 23 26C26 24 29 26 32 24C35 26 38 24 38 26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Dermis layer */}
    <path d="M14 33H38" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
    {/* Hair follicle */}
    <path d="M22 20V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M22 14C20 12 21 10 23 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    {/* Pore dots */}
    <circle cx="18" cy="22" r="1" fill="currentColor" fillOpacity="0.3"/>
    <circle cx="30" cy="22" r="1" fill="currentColor" fillOpacity="0.3"/>
    {/* Glass reflection */}
    <path d="M18 16C19 15 21 15 22 16" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
  </svg>
);

const ENTIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <circle cx="32" cy="20" r="10" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <path d="M26 18C26 18 28 22 32 22C36 22 38 18 38 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M32 30V42" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M22 36H42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M18 16C14 18 12 22 12 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M46 16C50 18 52 22 52 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <ellipse cx="32" cy="50" rx="8" ry="4" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
);

const PaediatricsIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <circle cx="32" cy="18" r="8" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <path d="M32 26C24 26 18 32 18 40V50H46V40C46 32 40 26 32 26Z" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <circle cx="28" cy="16" r="1.5" fill="currentColor"/>
    <circle cx="36" cy="16" r="1.5" fill="currentColor"/>
    <path d="M29 20C30 21 34 21 35 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M14 36H18M46 36H50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const SpineIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <path d="M32 8V56" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    <ellipse cx="32" cy="14" rx="6" ry="3" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="32" cy="24" rx="7" ry="3.5" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="32" cy="34" rx="8" ry="4" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="32" cy="44" rx="7" ry="3.5" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="32" cy="52" rx="5" ry="2.5" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
);

const OncologyIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <circle cx="32" cy="32" r="16" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <circle cx="32" cy="32" r="6" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.15"/>
    <path d="M32 8V16M32 48V56M8 32H16M48 32H56" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M15 15L21 21M43 43L49 49M49 15L43 21M21 43L15 49" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SurgeryIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <path d="M16 10L28 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M48 10L36 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M28 40L32 52L36 40" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="32" cy="32" r="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.2"/>
    <path d="M12 8H20M44 8H52" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const PlasticIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <circle cx="32" cy="24" r="12" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <path d="M24 22C26 20 28 20 30 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M34 22C36 20 38 20 40 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M28 28C30 30 34 30 36 28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M20 36L16 48M44 36L48 48" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M26 36V52H38V36" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
);

const DiabetologyIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
    <path d="M32 8C32 8 16 24 16 38C16 48 23 56 32 56C41 56 48 48 48 38C48 24 32 8 32 8Z" stroke="currentColor" strokeWidth="2.5" fill="none"/>
    <path d="M24 36H40" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M32 28V44" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const SpecialitiesSection = () => {
  const { t, language } = useLanguage();
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const specialities = [
    { key: 'obgyn', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/3ps59rc0_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_52_50%20PM.png', color: '#e91e8c' },
    { key: 'urology', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/grtqloyo_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_54_39%20PM.png', color: '#0097a7' },
    { key: 'cardio', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/ptqugzcp_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_19_28%20PM.png', color: '#c41e3a' },
    { key: 'ortho', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/txbmuc8p_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_22_06%20PM.png', color: '#f57c00' },
    { key: 'gastro', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/4q730mvt_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_23_51%20PM.png', color: '#7b1fa2' },
    { key: 'ent', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/ltb3z64s_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_27_55%20PM.png', color: '#00838f' },
    { key: 'paediatrics', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/82ns613g_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_40_38%20PM.png', color: '#f06292' },
    { key: 'spine', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/grbpn5uk_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_47_34%20PM.png', color: '#5c6bc0' },
    { key: 'diabetology', image: 'https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/pj1854id_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2002_50_17%20PM.png', color: '#00897b' },
  ];

  const visibleSpecialities = showAll ? specialities : specialities.slice(0, 8);

  return (
    <section id="specialities" className="py-16 lg:py-20 bg-white" data-testid="specialities-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 bg-[#0066b3]/8 text-[#0066b3] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
            {language === 'ta' ? 'எங்கள் சிறப்புகள்' : 'Our Specialities'}
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f172a] tracking-tight">
            {language === 'ta' ? 'சென்னையின் சிறந்த மருத்துவ சிறப்புகள்' : 'Best Medical Specialities in Chennai'}
          </h2>
        </div>

        {/* Specialities Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {visibleSpecialities.map((spec) => {
            const name = t(`specialities.departments.${spec.key}.name`);
            const desc = t(`specialities.departments.${spec.key}.desc`);

            return (
              <div
                key={spec.key}
                className="group relative bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 card-3d card-glow"
                data-testid={`speciality-card-${spec.key}`}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty('--mouse-x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
                  e.currentTarget.style.setProperty('--mouse-y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
                }}
              >
                {/* Image */}
                <div className="relative h-36 overflow-hidden">
                  <img src={spec.image} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
                {/* Content */}
                <div className="p-4">
                  <h3 className="text-sm font-bold text-[#0f172a] mb-1 group-hover:text-[#0066b3] transition-colors">{name}</h3>
                  <p className="text-xs text-[#64748b] line-clamp-2 mb-2 leading-relaxed">{desc}</p>
                  <button
                    className="inline-flex items-center gap-1 text-xs font-semibold transition-all"
                    style={{ color: spec.color }}
                    onClick={() => setSelectedSpecialty(spec.key)}
                    data-testid={`speciality-learn-more-${spec.key}`}
                  >
                    {language === 'ta' ? 'மேலும் அறிய' : 'Learn More'}
                    <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" style={{ backgroundColor: spec.color }}></div>
              </div>
            );
          })}
        </div>

        {/* View All / Show Less Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0066b3] text-white rounded-lg font-semibold text-sm hover:bg-[#004a82] transition-colors shadow-md"
            data-testid="view-all-specialities-btn"
          >
            {showAll
              ? (language === 'ta' ? 'குறைவாகக் காட்டு' : 'Show Less')
              : (language === 'ta' ? `அனைத்தையும் காண்க (${specialities.length})` : `View All ${specialities.length} Departments`)
            }
            <ArrowRight size={16} className={showAll ? 'rotate-[-90deg]' : ''} />
          </button>
        </div>

        {/* Specialty Modal */}
        {selectedSpecialty && (
          <SpecialtyModal specialtyKey={selectedSpecialty} onClose={() => setSelectedSpecialty(null)} />
        )}
      </div>
    </section>
  );
};

export default SpecialitiesSection;
